import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import { refreshSchool, type Pupil, type School, type SchoolClass } from './schoolApi';

function ClassPicker({ classes, chosen, onChange, idPrefix }: {
  classes: SchoolClass[];
  chosen: string[];
  onChange: (ids: string[]) => void;
  idPrefix: string;
}) {
  const t = useT();
  if (classes.length === 0) return <p className="field-hint">{t('No classes yet. Add them under Classes.')}</p>;
  return (
    <fieldset className="school-checks">
      <legend>{t('Classes')}</legend>
      {classes.map((c) => (
        <label className="check" key={c.id} htmlFor={`${idPrefix}-${c.id}`}>
          <input
            id={`${idPrefix}-${c.id}`}
            type="checkbox"
            checked={chosen.includes(c.id)}
            onChange={(e) => onChange(e.target.checked ? [...chosen, c.id] : chosen.filter((x) => x !== c.id))}
          />
          {c.name}
        </label>
      ))}
    </fieldset>
  );
}

export default function PupilsTab({ school }: { school: School }) {
  const t = useT();
  const admin = school.role === 'ADMIN';
  const [pupils, setPupils] = useState<Pupil[] | null>(null);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [newClasses, setNewClasses] = useState<string[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editClasses, setEditClasses] = useState<string[]>([]);
  const [removing, setRemoving] = useState<Pupil | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api<Pupil[]>('/school/pupils', { token: true }),
        api<SchoolClass[]>('/school/classes', { token: true }),
      ]);
      setPupils(p);
      setClasses(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load the pupils.'));
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? '';

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      await load();
      refreshSchool();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That did not work. Please try again.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const ok = await run(() => api('/school/pupils', { method: 'POST', token: true, body: { name: name.trim(), classIds: newClasses } }));
    if (ok) { setName(''); setNewClasses([]); }
  }

  async function save(id: string) {
    const ok = await run(() => api(`/school/pupils/${id}`, { method: 'PATCH', token: true, body: { name: editName.trim(), classIds: editClasses } }));
    if (ok) setEditing(null);
  }

  return (
    <div className="spaced">
      <div className="card">
        <h3>{t('Pupils')}</h3>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {admin
            ? t('{n} of {limit} pupil places used.', { n: school.pupils, limit: school.pupilLimit })
            : t('You see the pupils in the classes you teach. A school admin adds pupils and changes classes.')}
        </p>
        {error && <div className="error" role="alert">{error}</div>}
        {pupils === null ? (
          <p className="muted">{t('Loading…')}</p>
        ) : pupils.length === 0 ? (
          <p className="muted">{admin ? t('No pupils yet. Add the first one below.') : t('No pupils to show.')}</p>
        ) : (
          <ul className="list">
            {pupils.map((p) => (
              <li className="list-item" key={p.id}>
                {editing === p.id ? (
                  <div className="spaced" style={{ flex: 1 }}>
                    <div className="field">
                      <label htmlFor={`pupil-name-${p.id}`}>{t('Name')}</label>
                      <input id={`pupil-name-${p.id}`} value={editName} maxLength={80} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                    <ClassPicker classes={classes} chosen={editClasses} onChange={setEditClasses} idPrefix={`pupil-class-${p.id}`} />
                    <div className="toolbar">
                      <button type="button" className="btn" onClick={() => void save(p.id)} disabled={busy || !editName.trim()}>{t('Save')}</button>
                      <button type="button" className="btn ghost" onClick={() => setEditing(null)} disabled={busy}>{t('Cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="list-item-text">
                      <div className="name">{p.name}</div>
                      <div className="muted">{p.classIds.length ? p.classIds.map(className).join(', ') : t('Not in a class')}</div>
                    </div>
                    {admin && (
                      <div className="toolbar">
                        <button type="button" className="btn secondary" onClick={() => { setEditing(p.id); setEditName(p.name); setEditClasses(p.classIds); }}>
                          {t('Change')}
                        </button>
                        <button type="button" className="btn quiet-danger" onClick={() => setRemoving(p)}>{t('Remove')}</button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {admin && (
        <form className="card spaced" onSubmit={add}>
          <h3>{t('Add a pupil')}</h3>
          <div className="field">
            <label htmlFor="pupil-new-name">{t('Name')}</label>
            <input id="pupil-new-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            <span className="field-hint">{t('A first name and the first letter of the surname is usually enough.')}</span>
          </div>
          <ClassPicker classes={classes} chosen={newClasses} onChange={setNewClasses} idPrefix="pupil-new-class" />
          <div className="toolbar">
            <button type="submit" className="btn" disabled={busy || !name.trim()}>{t('Add pupil')}</button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={removing !== null}
        title={t('Remove {name}?', { name: removing?.name ?? '' })}
        message={t('This removes the pupil and everything recorded about their practice. This cannot be undone.')}
        confirmLabel={t('Remove pupil')}
        destructive
        onConfirm={() => {
          const p = removing;
          setRemoving(null);
          if (p) void run(() => api(`/school/pupils/${p.id}`, { method: 'DELETE', token: true }));
        }}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
